import { LayaGL } from "../../layagl/LayaGL";
import { ShaderCompile } from "../../webgl/utils/ShaderCompile";
import { SubShader } from "./SubShader";
export var ShaderFeatureType;
(function (ShaderFeatureType) {
    ShaderFeatureType[ShaderFeatureType["None"] = -1] = "None";
    ShaderFeatureType[ShaderFeatureType["Default"] = 0] = "Default";
    ShaderFeatureType[ShaderFeatureType["D3"] = 1] = "D3";
    ShaderFeatureType[ShaderFeatureType["D2_primitive"] = 2] = "D2_primitive";
    ShaderFeatureType[ShaderFeatureType["D2_TextureSV"] = 3] = "D2_TextureSV";
    ShaderFeatureType[ShaderFeatureType["D2_BaseRenderNode2D"] = 4] = "D2_BaseRenderNode2D";
    ShaderFeatureType[ShaderFeatureType["PostProcess"] = 5] = "PostProcess";
    ShaderFeatureType[ShaderFeatureType["Sky"] = 6] = "Sky";
    ShaderFeatureType[ShaderFeatureType["Effect"] = 7] = "Effect";
})(ShaderFeatureType || (ShaderFeatureType = {}));
export class Shader3D {
    static init() {
        Shader3D._configDefineValues = LayaGL.unitRenderModuleDataFactory.createDefineDatas();
        Shader3D.SHADERDEFINE_REMAP_POSITIONZ = Shader3D.getDefineByName("REMAP_Z");
        Shader3D.SHADERDEFINE_LOD_TEXTURE_SAMPLE = Shader3D.getDefineByName("LOD_TEXTURE_SAMPLE");
        Shader3D.SHADERDEFINE_BREAK_TEXTURE_SAMPLE = Shader3D.getDefineByName("BREAK_TEXTURE_SAMPLE");
        if (LayaGL.renderEngine._remapZ)
            Shader3D._configDefineValues.add(Shader3D.SHADERDEFINE_REMAP_POSITIONZ);
        if (LayaGL.renderEngine._lodTextureSample)
            Shader3D._configDefineValues.add(Shader3D.SHADERDEFINE_LOD_TEXTURE_SAMPLE);
        if (LayaGL.renderEngine._breakTextureSample)
            Shader3D._configDefineValues.add(Shader3D.SHADERDEFINE_BREAK_TEXTURE_SAMPLE);
        Shader3D._compileDefineDatas = LayaGL.unitRenderModuleDataFactory.createDefineDatas();
    }
    static _getNamesByDefineData(defineData, out) {
        LayaGL.renderEngine.getNamesByDefineData(defineData, out);
        return out;
    }
    static getDefineByName(name) {
        return LayaGL.renderEngine.getDefineByName(name);
    }
    static propertyNameToID(name) {
        return LayaGL.renderEngine.propertyNameToID(name);
    }
    static propertyIDToName(id) {
        return LayaGL.renderEngine.propertyIDToName(id);
    }
    static addInclude(fileName, txt, allowReplace) {
        ShaderCompile.addInclude(fileName, txt, allowReplace);
    }
    static compileShaderByDefineNames(shaderName, subShaderIndex, passIndex, defineNames, nodeCommonMap, additionMap, is2D, attributeLocations) {
        var shader = Shader3D.find(shaderName);
        if (shader) {
            var subShader = shader.getSubShaderAt(subShaderIndex);
            if (subShader) {
                var pass = subShader._passes[passIndex];
                if (pass) {
                    pass.nodeCommonMap = nodeCommonMap;
                    pass.additionShaderData = additionMap;
                    pass.attributeLocations = new Set(attributeLocations);
                    var compileDefineDatas = Shader3D._compileDefineDatas;
                    Shader3D._configDefineValues.cloneTo(compileDefineDatas);
                    for (let n of defineNames)
                        compileDefineDatas.add(Shader3D.getDefineByName(n));
                    pass.withCompile(compileDefineDatas, is2D);
                    return true;
                }
            }
        }
        return false;
    }
    static compileShaderByBin(shaderName, subShaderIndex, passIndex, defineNames, nodeCommonMap, cacheBuffer) {
        var shader = Shader3D.find(shaderName);
        if (shader) {
            var subShader = shader.getSubShaderAt(subShaderIndex);
            if (subShader) {
                var pass = subShader._passes[passIndex];
                pass.nodeCommonMap = nodeCommonMap;
                if (pass) {
                    var compileDefineDatas = Shader3D._compileDefineDatas;
                    compileDefineDatas.clear();
                    for (let n of defineNames)
                        compileDefineDatas.add(Shader3D.getDefineByName(n));
                    pass.withComplieByBin(compileDefineDatas, false, cacheBuffer);
                }
            }
        }
    }
    static add(name, enableInstancing = false, supportReflectionProbe = false) {
        return Shader3D._preCompileShader[name] = new Shader3D(name, enableInstancing, supportReflectionProbe);
    }
    static find(name) {
        return Shader3D._preCompileShader[name];
    }
    static parse(data, basePath) {
        var _a;
        if (!data.name)
            console.warn("shader name is empty", data);
        if (!data.uniformMap)
            console.warn(`${data.name}: uniformMap is empty`);
        let shader = Shader3D.add(data.name, data.enableInstancing, data.supportReflectionProbe);
        shader._supportVolumetricGI = data.supportVolumetricGI;
        shader.shaderType = data.shaderType;
        let subshader = new SubShader(data.attributeMap ? data.attributeMap : SubShader.DefaultAttributeMap, data.uniformMap, data.defaultValue);
        shader.addSubShader(subshader);
        let passDataArray = data.shaderPass;
        for (var i in passDataArray) {
            let passData = passDataArray[i];
            if (!passData.VS) {
                console.warn(`${data.name}: VS of pass ${i} is empty`);
                continue;
            }
            if (!passData.FS) {
                console.warn(`${data.name}: FS of pass ${i} is empty`);
                continue;
            }
            let shaderPass = subshader._addShaderPass(ShaderCompile.compile(passData.VS, passData.FS, basePath), passData.pipeline);
            shaderPass.statefirst = (_a = passData.statefirst) !== null && _a !== void 0 ? _a : false;
            ShaderCompile.getRenderState(passData.renderState, shaderPass.renderState);
        }
        return shader;
    }
    get name() {
        return this._name;
    }
    constructor(name, enableInstancing, supportReflectionProbe) {
        this._enableInstancing = false;
        this._supportReflectionProbe = false;
        this._supportVolumetricGI = false;
        this._subShaders = [];
        this.shaderType = ShaderFeatureType.None;
        this._name = name;
        this._enableInstancing = enableInstancing;
        this._supportReflectionProbe = supportReflectionProbe;
    }
    addSubShader(subShader) {
        this._subShaders.push(subShader);
        subShader._owner = this;
        subShader.moduleData.enableInstance = this._enableInstancing;
        subShader.moduleData.shaderName = this._name;
    }
    getSubShaderAt(index) {
        return this._subShaders[index];
    }
}
Shader3D.PERIOD_CUSTOM = 0;
Shader3D.PERIOD_MATERIAL = 1;
Shader3D.PERIOD_SPRITE = 2;
Shader3D.PERIOD_CAMERA = 3;
Shader3D.PERIOD_SCENE = 4;
Shader3D._propertyNameMap = {};
Shader3D._preCompileShader = {};
Shader3D.debugMode = false;
